import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import { useNavigate } from "react-router-dom";
import { routeConfigs } from "../../../navigation/routeConfig";
import { BODY_FONT, cardSx, DISPLAY_FONT, INK, INK_SOFT, PRIMARY } from "../dashboardTokens";

interface KpiItem {
  key: string;
  label: string;
  value: string | number;
  hint?: string;
  icon: React.ReactNode;
  onClick?: () => void;
}

interface KpiCardsProps {
  wishlistCount: number;
  pendingOrdersCount: number;
  monthlyVolumeLabel: string;
  complianceLabel: string;
  complianceHint?: string;
  loading?: boolean;
}

const KpiCard: React.FC<{ item: KpiItem; loading?: boolean }> = ({ item, loading }) => (
  <Box
    onClick={item.onClick}
    sx={{
      ...cardSx,
      p: 2.25,
      height: "100%",
      cursor: item.onClick ? "pointer" : "default",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      "&:hover": item.onClick
        ? {
            borderColor: "#B8C9E8",
            boxShadow: "0 4px 14px rgba(37, 99, 235, 0.08)",
          }
        : undefined,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
      <Typography
        sx={{
          fontFamily: BODY_FONT,
          fontSize: 13,
          fontWeight: 600,
          color: INK_SOFT,
          letterSpacing: 0.2,
        }}
      >
        {item.label}
      </Typography>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: "rgba(37, 99, 235, 0.08)",
          color: PRIMARY,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {item.icon}
      </Box>
    </Box>
    {loading ? (
      <Skeleton width="40%" height={36} />
    ) : (
      <Typography
        sx={{
          fontFamily: DISPLAY_FONT,
          fontSize: 28,
          fontWeight: 700,
          color: INK,
          lineHeight: 1.15,
        }}
      >
        {item.value}
      </Typography>
    )}
    {item.hint && (
      <Typography sx={{ mt: 0.75, fontFamily: BODY_FONT, fontSize: 12, color: INK_SOFT }}>
        {item.hint}
      </Typography>
    )}
  </Box>
);

export const KpiCards: React.FC<KpiCardsProps> = ({
  wishlistCount,
  pendingOrdersCount,
  monthlyVolumeLabel,
  complianceLabel,
  complianceHint,
  loading,
}) => {
  const navigate = useNavigate();

  const items: KpiItem[] = [
    {
      key: "wishlist",
      label: "Active Wishlist Items",
      value: wishlistCount,
      hint: "Track price alerts",
      icon: <FavoriteBorderIcon fontSize="small" />,
      onClick: () => navigate(routeConfigs.wishlist.path),
    },
    {
      key: "pending",
      label: "Pending Orders",
      value: pendingOrdersCount,
      hint: "Sell orders awaiting action",
      icon: <PendingActionsIcon fontSize="small" />,
      onClick: () => navigate(routeConfigs.openOrders.path),
    },
    {
      key: "volume",
      label: "Monthly Volume",
      value: monthlyVolumeLabel,
      hint: "Buy + sell this month",
      icon: <TrendingUpIcon fontSize="small" />,
      onClick: () => navigate(routeConfigs.buyHistory.path),
    },
    {
      key: "compliance",
      label: "Compliance Status",
      value: complianceLabel,
      hint: complianceHint ?? "Account verification",
      icon: <VerifiedUserIcon fontSize="small" />,
      onClick: () => navigate(routeConfigs.pharmacyProfile.path),
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "1fr 1fr",
          lg: "repeat(4, 1fr)",
        },
        gap: 2,
      }}
    >
      {items.map((item) => (
        <KpiCard key={item.key} item={item} loading={loading} />
      ))}
    </Box>
  );
};

export default KpiCards;
